import React from "react";
import type { SymbolCatalogStatusDto } from "../models/edgeMonitorAdminModels";

const FONT_SORA = "'Sora', system-ui, -apple-system, sans-serif";
const FONT_MONO = "'JetBrains Mono', 'SF Mono', Menlo, Consolas, monospace";

const COLORS = {
  green: "#2D8C4E",
  greenBg: "#E8EFE6",
  terracotta: "#D4572B",
  terracottaBg: "#FCEBE3",
  greyDot: "#8C9099",
  greyBg: "#F0EFE9",
  primaryText: "#1A1A1A",
  dimText: "#6B6B6B",
};

export interface Zone1CatalogTileProps {
  data: SymbolCatalogStatusDto | null | undefined;
  loadingNoData: boolean;
  notAvailable: boolean;
}

/**
 * Format an ISO timestamp into a compact date — "2026-05-10". Falls back to
 * "—" on invalid input. Intentionally drops time-of-day; for staff debug the
 * fact that it was seeded today vs. last week is the actionable signal.
 */
const formatDate = (iso: string | null | undefined): string => {
  if (!iso) return "—";
  const hasTz = /(Z|[+-]\d{2}:?\d{2})$/.test(iso);
  const ms = Date.parse(hasTz ? iso : iso + "Z");
  if (Number.isNaN(ms)) return "—";
  try {
    return new Date(ms).toISOString().slice(0, 10);
  } catch {
    return "—";
  }
};

const Zone1CatalogTile: React.FC<Zone1CatalogTileProps> = ({
  data,
  loadingNoData,
  notAvailable,
}) => {
  let bg = COLORS.greyBg;
  let textColor = COLORS.dimText;
  let dot = COLORS.greyDot;
  let primary: string;
  let secondary: string;
  let bold = false;

  if (loadingNoData) {
    primary = "Loading…";
    secondary = " ";
  } else if (notAvailable && !data) {
    primary = "No access";
    secondary = " ";
  } else if (data && data.isSeeded) {
    bg = COLORS.greenBg;
    textColor = COLORS.green;
    dot = COLORS.green;
    bold = true;
    const rowsLabel = data.rowCount === 1 ? "1 row" : `${data.rowCount} rows`;
    primary = `Catalog · ${rowsLabel}`;
    secondary = `seeded ${formatDate(data.lastSeededAt)}`;
  } else if (data) {
    bg = COLORS.terracottaBg;
    textColor = COLORS.terracotta;
    dot = COLORS.terracotta;
    bold = true;
    primary = "Catalog · 0 rows";
    secondary = "not seeded";
  } else {
    primary = "—";
    secondary = " ";
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "6px",
      }}
    >
      <div
        style={{
          fontFamily: FONT_MONO,
          fontSize: "10px",
          letterSpacing: "0.1em",
          color: COLORS.dimText,
          textTransform: "uppercase",
        }}
      >
        Symbol Catalog
      </div>
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "8px",
          padding: "5px 11px",
          borderRadius: "12px",
          background: bg,
          alignSelf: "flex-start",
        }}
      >
        <span
          style={{
            width: "8px",
            height: "8px",
            borderRadius: "50%",
            background: dot,
            flexShrink: 0,
          }}
        />
        <span
          style={{
            fontFamily: FONT_SORA,
            fontSize: "12px",
            fontWeight: bold ? 600 : 500,
            color: textColor,
            letterSpacing: "0.02em",
          }}
        >
          {primary}
        </span>
      </div>
      <div
        style={{
          fontFamily: FONT_MONO,
          fontSize: "11px",
          color: COLORS.dimText,
          letterSpacing: "0.04em",
          minHeight: "14px",
        }}
      >
        {secondary}
      </div>
    </div>
  );
};

export default Zone1CatalogTile;
